import { window } from "../window";

/**
 * focus provides utility methods to set focus on an element inside a container.
 */

export const focusFirstElement = (scopeSelector: string, doc: Document = window.document): void => {
  const focusableItemsList = getFocusableItems(scopeSelector, doc);

  if (focusableItemsList.length && doc.activeElement !== focusableItemsList[0]) {
    (focusableItemsList[0] as HTMLElement).focus();
  }
};

export const focusLastElement = (scopeSelector: string, doc: Document = window.document): void => {
  const focusableItemsList = getFocusableItems(scopeSelector, doc);
  const itemCount = focusableItemsList.length;

  if (itemCount && doc.activeElement !== focusableItemsList[itemCount - 1]) {
    (focusableItemsList.at(-1) as HTMLElement).focus();
  }
};

export function getFocusableItems(
  scopeSelector: string,
  doc: Document = window.document
): Element[] {
  const scopeElement = doc.querySelector(scopeSelector);
  let focusableItemsList: Element[] = [];

  if (!isAnyAncestorHidden(scopeElement)) {
    focusableItemsList = getAllFocusableItems(scopeSelector);
    focusableItemsList = filterHiddenInputs(focusableItemsList);
    focusableItemsList = filterItemsRemovedFromTabOrder(focusableItemsList);
    focusableItemsList = filterHiddenItems(focusableItemsList);
    focusableItemsList = filterAriaHiddenItems(focusableItemsList);
    focusableItemsList = filterDisabledItems(focusableItemsList);
  }

  return focusableItemsList;
}

export function getFocusElement(doc: Document = window.document): Element | null {
  return doc.activeElement;
}

export function setFocusElement(element: HTMLElement | null): void {
  element?.focus?.();
}

function getAllFocusableItems(scopeSelector: string): Element[] {
  const selectors = `${scopeSelector} a[href], ${scopeSelector} button, ${scopeSelector} [tabIndex="0"], 
        ${scopeSelector} input, ${scopeSelector} select, ${scopeSelector} textarea`;

  return Array.from(window.document.querySelectorAll(selectors));
}

function filterHiddenInputs(itemsList: Element[]): Element[] {
  return itemsList.filter((item) => item.getAttribute("type") !== "hidden");
}

function filterItemsRemovedFromTabOrder(itemsList: Element[]): Element[] {
  return itemsList.filter((item) => (item as HTMLElement).tabIndex !== -1);
}

function filterHiddenItems(itemsList: Element[]): Element[] {
  return itemsList.filter((item) => !isAnyAncestorHidden(item));
}

function isAnyAncestorHidden(workingElement: Element | null): boolean {
  const ancestors = getAncestors(workingElement);

  return ancestors.some(
    (ancestor) =>
      window.getComputedStyle(ancestor as HTMLElement).display === "none" ||
      window.getComputedStyle(ancestor as HTMLElement).visibility === "hidden"
  );
}

function getAncestors(element: Element | null): Element[] {
  const ancestors: Element[] = [];
  let currentElement: Element | ParentNode | null = element;

  while (currentElement && currentElement !== window.document) {
    ancestors.push(currentElement as Element);
    currentElement = (currentElement as Element).parentNode;
  }

  return ancestors;
}

function filterAriaHiddenItems(itemsList: Element[]): Element[] {
  return itemsList.filter((item) => item.getAttribute("aria-hidden") !== "true");
}

function filterDisabledItems(itemsList: Element[]): Element[] {
  return itemsList.filter((item) => !(item as HTMLInputElement).disabled);
}
